import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useAdminInactivityTimeout } from '@/hooks/useAdminInactivityTimeout';
import { AdminInactivityWarning } from '@/components/admin/AdminInactivityWarning';
import { AdminNotificationBell } from '@/components/admin/AdminNotificationBell';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Building2,
  LogOut,
  User,
  Menu,
  Stethoscope,
  ShieldCheck,
  History,
  ChevronDown,
  Settings,
  ArrowRightLeft,
  BarChart3,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { SkipToContent } from '@/components/SkipToContent';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { user, signOut } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [impersonatedAgency, setImpersonatedAgency] = useState<{ id: string; name: string; code: string } | null>(null);

  // Check for impersonation mode
  const isImpersonating = searchParams.get('impersonate') === 'agency';
  const impersonatedAgencyId = searchParams.get('aid');
  const impersonationSessionId = searchParams.get('sid');

  // Fetch impersonated agency details
  useEffect(() => {
    const fetchAgencyDetails = async () => {
      if (isImpersonating && impersonatedAgencyId) {
        const { data } = await supabase
          .from('agencies')
          .select('id, name, code')
          .eq('id', impersonatedAgencyId)
          .single();
        
        if (data) {
          setImpersonatedAgency(data);
        }
      } else {
        setImpersonatedAgency(null);
      }
    };
    fetchAgencyDetails();
  }, [isImpersonating, impersonatedAgencyId]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/login');
  }, [signOut, navigate]);

  // Check if user selected "Remember me" - extends timeout to 7 days
  const rememberMe = typeof window !== 'undefined' && localStorage.getItem('admin_remember_me') === 'true';
  const timeoutMs = rememberMe ? 7 * 24 * 60 * 60 * 1000 : 30 * 60 * 1000; // 7 days vs 30 min
  const warningMs = rememberMe ? 5 * 60 * 1000 : 2 * 60 * 1000; // 5 min vs 2 min warning

  const { showWarning, remainingSeconds, extendSession } = useAdminInactivityTimeout({
    timeoutMs,
    warningMs,
    onTimeout: handleSignOut,
  });

  // Get role-specific dashboard route
  const getDashboardRoute = () => {
    if (isImpersonating) return '/agency/dashboard';
    switch (user?.role) {
      case 'super_admin': return '/super-admin/dashboard';
      case 'agency_admin': return '/agency/dashboard';
      case 'supervisor': return '/supervisor/dashboard';
      default: return '/agency/dashboard';
    }
  };

  // Navigation items based on role
  const getMainNavigation = () => {
    const dashboardItem = { name: 'Dashboard', href: getDashboardRoute(), icon: LayoutDashboard };
    
    // Supervisor only sees Dashboard (patients are shown within it)
    if (user?.role === 'supervisor' && !isImpersonating) {
      return [
        dashboardItem,
        { name: 'Settings', href: '/admin/settings', icon: Settings },
      ];
    }

    // Agency Admin and Super Admin (or impersonating) see full navigation
    return [
      dashboardItem,
      { name: 'Patients', href: '/admin/patients', icon: Users },
      { name: 'Supervisors', href: '/admin/supervisors', icon: ShieldCheck },
      { name: 'Transfers', href: '/admin/transfers', icon: ArrowRightLeft },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
      { name: 'Billing', href: '/admin/billing', icon: CreditCard },
      { name: 'Settings', href: '/admin/settings', icon: Settings },
    ];
  };

  const mainNavigation = getMainNavigation();

  // Hide admin navigation when impersonating as agency admin
  const adminNavigation = (user?.role === 'super_admin' && !isImpersonating) ? [
    { name: 'Agencies', href: '/admin/agencies', icon: Building2 },
    { name: 'User Roles', href: '/admin/user-roles', icon: ShieldCheck },
    { name: 'Audit Logs', href: '/admin/audit-logs', icon: History },
    { name: 'Diagnostics', href: '/admin/diagnostics', icon: Activity },
  ] : [];

  const [adminSectionOpen, setAdminSectionOpen] = useState(
    adminNavigation.some(item => location.pathname === item.href)
  );

  const userInitials = user?.email.split('@')[0].slice(0, 2).toUpperCase() || 'A';

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center px-6 py-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 gradient-hero rounded-lg flex items-center justify-center">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">CheckFirst</h1>
            <p className="text-xs text-muted-foreground">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Agency Info - Show impersonated agency when in impersonation mode */}
      {(impersonatedAgency || user?.agency) && (
        <div className={cn(
          "px-6 py-3 border-b border-border",
          impersonatedAgency ? "bg-amber-100/50 dark:bg-amber-900/20" : "bg-secondary/20"
        )}>
          {impersonatedAgency ? (
            <>
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1">Viewing as Agency Admin</p>
              <p className="text-sm font-medium text-foreground">{impersonatedAgency.name}</p>
              <p className="text-xs text-muted-foreground">Code: {impersonatedAgency.code}</p>
            </>
          ) : user?.agency && (
            <>
              <p className="text-sm font-medium text-foreground">{user.agency.name}</p>
              <p className="text-xs text-muted-foreground">Code: {user.agency.code}</p>
            </>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
        {/* Main Navigation */}
        <div className="space-y-1">
          <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Main
          </p>
          {mainNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={getNavHref(item.href)}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon className="mr-3 h-4 w-4" />
                {item.name}
              </Link>
            );
          })}
        </div>

        {/* Admin Section - Super Admin Only */}
        {adminNavigation.length > 0 && (
          <div className="pt-4 space-y-1">
            <button
              onClick={() => setAdminSectionOpen(!adminSectionOpen)}
              className="w-full flex items-center justify-between px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
            >
              <span>Administration</span>
              <ChevronDown className={cn(
                'h-4 w-4 transition-transform',
                adminSectionOpen && 'rotate-180'
              )} />
            </button>
            {adminSectionOpen && (
              <div className="space-y-1 mt-2">
                {adminNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <item.icon className="mr-3 h-4 w-4" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* User Info */}
      <div className="border-t border-border p-4">
        <div className="flex items-center space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {userInitials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.email}
            </p>
        <p className="text-xs text-muted-foreground capitalize">
              {isImpersonating ? 'Agency Admin (Impersonating)' : user?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper to preserve impersonation params when navigating
  const getNavHref = (baseHref: string) => {
    if (isImpersonating && impersonatedAgencyId && impersonationSessionId) {
      return `${baseHref}?impersonate=agency&aid=${impersonatedAgencyId}&sid=${impersonationSessionId}`;
    }
    return baseHref;
  };

  return (
    <div className={cn("min-h-screen bg-background", isImpersonating && "pt-12")}>
      <SkipToContent />
      {/* Impersonation Banner - always render, let the component handle visibility */}
      <ImpersonationBanner />

      {/* Mobile sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <div className={cn(
        "hidden lg:fixed lg:flex lg:w-72 lg:flex-col",
        isImpersonating ? "lg:top-12 lg:h-[calc(100vh-3rem)]" : "lg:inset-y-0"
      )}>
        <div className="flex flex-col h-full bg-card border-r border-border shadow-sm">
          <SidebarContent />
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <div className={cn(
          "sticky z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8",
          isImpersonating ? "top-12" : "top-0"
        )}>
          <Button 
            variant="ghost" 
            size="sm" 
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            
            {/* Notifications & User menu */}
            <div className="flex items-center gap-x-2 lg:gap-x-4">
              <AdminNotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user?.email}</p>
                      <p className="text-xs leading-none text-muted-foreground capitalize">
                        {user?.role?.replace('_', ' ')}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/admin/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main id="main-content" tabIndex={-1} className="py-8 outline-none">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Inactivity Warning Modal */}
      <AdminInactivityWarning
        open={showWarning}
        remainingSeconds={remainingSeconds}
        onExtend={extendSession}
        onLogout={handleSignOut}
      />
    </div>
  );
};

export default AdminLayout;